import { DateTime } from 'luxon'
import { startOfUsWeek } from '../schedule-date-ranges'
import {
  buildBlockedByDay,
  buildRecurringByDayIndex,
} from 'pages/pga-coach/settings/availability/availability-tab/utils'

const timeStringToFractionalHour = timeStr => {
  if (!timeStr) return null
  const dt = DateTime.fromISO(timeStr)
  if (!dt.isValid) return null
  return dt.hour + dt.minute / 60
}

export const getSelectedFacilityId = selectedFacility => {
  if (selectedFacility == null || selectedFacility === '') return 'all'
  if (typeof selectedFacility === 'object') {
    return selectedFacility.id || 'all'
  }
  return selectedFacility
}

export const isAllFacilitiesSelected = selectedFacility =>
  getSelectedFacilityId(selectedFacility) === 'all'

export const filterAvailabilitiesForFacility = (
  availabilities,
  selectedFacility,
) => {
  if (!availabilities?.length) return []
  if (isAllFacilitiesSelected(selectedFacility)) return availabilities
  const facilityId = getSelectedFacilityId(selectedFacility)
  return availabilities.filter(
    availability => availability.coachFacility?.id === facilityId,
  )
}

export const filterOverridesForFacility = (overrides, selectedFacility) => {
  if (!overrides?.length) return []
  if (isAllFacilitiesSelected(selectedFacility)) return overrides
  const facilityId = getSelectedFacilityId(selectedFacility)
  return overrides.filter(override => override.coachFacility?.id === facilityId)
}

export const filterBlocksForFacility = (blocks, selectedFacility) => {
  if (!blocks?.length) return []
  if (isAllFacilitiesSelected(selectedFacility)) return blocks

  // coachAvailabilityBlockTimes is not facility-scoped on the API yet.
  // Skip overlay blocked segments for a single facility; schedule bookings
  // remain the source of truth until coachFacility exists on the type.
  const facilityId = getSelectedFacilityId(selectedFacility)
  return blocks.filter(block => block.coachFacility?.id === facilityId)
}

export const resolveBlockTimeZone = availabilities => {
  if (!availabilities?.length) return undefined
  const zones = availabilities.map(item => item.timeZone).filter(Boolean)
  if (!zones.length) return undefined
  if (zones.every(zone => zone === zones[0])) return zones[0]
  return undefined
}

export const emptyAvailabilityDay = () => ({
  recurring: [],
  override: [],
  blocked: [],
})

const fractionalHourToDateTime = (day, fractionalHour, zone) => {
  const hour = Math.floor(fractionalHour)
  const minute = Math.round((fractionalHour - hour) * 60)
  const dateTime = day.set({
    hour,
    minute,
    second: 0,
    millisecond: 0,
  })

  return zone ? dateTime.setZone(zone, { keepLocalTime: true }) : dateTime
}

export const blockedSegmentToBooking = (segment, day, zone) => {
  const zoneName = zone || day.zoneName
  const start = segment.allDay
    ? day.startOf('day')
    : fractionalHourToDateTime(day, segment.start, zoneName)
  const end = segment.allDay
    ? day.plus({ days: 1 }).startOf('day')
    : fractionalHourToDateTime(day, segment.end, zoneName)

  return {
    __typename: 'CoachAvailabilityBlockTime',
    id: `availability-block-${segment.block?.id || 'unknown'}-${start.toISO()}`,
    title: segment.block?.blockNote || 'Blocked time',
    startDateTime: start.toISO(),
    endDateTime: end.toISO(),
    timezone: zoneName,
  }
}

export const buildAvailabilityByDate = ({
  days,
  availabilities = [],
  overrides = [],
  blocks = [],
  selectedFacility,
}) => {
  const byDate = {}
  ;(days || []).forEach(day => {
    byDate[day.toISODate()] = emptyAvailabilityDay()
  })

  if (!days?.length) return byDate

  const scopedAvailabilities = filterAvailabilitiesForFacility(
    availabilities,
    selectedFacility,
  )
  const scopedOverrides = filterOverridesForFacility(
    overrides,
    selectedFacility,
  )

  scopedAvailabilities.forEach(availability => {
    const recurringByDay = buildRecurringByDayIndex(availability.timeSlots)
    days.forEach(day => {
      const weekdayIndex = day.weekday % 7
      const segments = recurringByDay[weekdayIndex] || []
      byDate[day.toISODate()].recurring.push(...segments)
    })
  })

  scopedOverrides.forEach(override => {
    const bucket = byDate[override.date]
    if (!bucket) return
    ;(override.timeSlots || []).forEach(timeSlot => {
      const start = timeStringToFractionalHour(timeSlot.startTime)
      const end = timeStringToFractionalHour(timeSlot.endTime)
      if (start == null || end == null) return
      bucket.override.push({ start, end, override })
    })
  })

  const scopedBlocks = filterBlocksForFacility(blocks, selectedFacility)

  const weekStart = startOfUsWeek(days[0])
  const zone = resolveBlockTimeZone(scopedAvailabilities)
  const blockedByDay = buildBlockedByDay(scopedBlocks, weekStart, zone)
  days.forEach(day => {
    const dayIndex = Math.floor(
      day.startOf('day').diff(weekStart.startOf('day'), 'days').days,
    )
    if (dayIndex >= 0 && dayIndex < 7) {
      byDate[day.toISODate()].blocked.push(...(blockedByDay[dayIndex] || []))
    }
  })

  return byDate
}
